import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { UpToThreeActions } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { DashboardAreasConfiguration, Page, WidgetAreasConfiguration } from '../types'
import EditorContent from './EditorContent'
import { EDIT_SEARCH_PARAMETER } from '../consts'
import InstructionsContent from './InstructionsContent'
import { useCookies } from 'src/Libs/cookies'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useTypedSelector } from '@e3dc-react/shell/State/RootReducer'
import { DASHBOARD_EDITOR_NEXT_PAGE_BUTTON_ID, DASHBOARD_EDITOR_SAVE_BUTTON_ID } from 'src/Libs/qa/ids'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

export interface DashboardEditorProps {
  onClose?: () => void
  place?: number
  dashboardAreasConfiguration: DashboardAreasConfiguration
  widgetAreasConfiguration: WidgetAreasConfiguration
  saveAreasConfiguration: (areas: DashboardAreasConfiguration, widgetAreas: WidgetAreasConfiguration) => Promise<void>
  isDynamicPowerPriceEnabled?: boolean
}

/**
 * Dashboard editor component
 */
const DashboardEditor: React.FC<DashboardEditorProps> = ({
  onClose,
  dashboardAreasConfiguration,
  widgetAreasConfiguration,
  saveAreasConfiguration,
  place,
  isDynamicPowerPriceEnabled,
}) => {
  const { t } = useCustomTranslation()
  const [page, setPage] = useState<Page>(Page.Instruction)
  const { cookies, setHideDashboardEditorInstructions } = useCookies()
  const [doNotShowAgain, setDoNotShowAgain] = useState<boolean>(false)
  const [dashboardAreasConfigurationState, setDashboardAreasConfigurationState] = useState<DashboardAreasConfiguration>(dashboardAreasConfiguration)
  const [widgetAreasConfigurationState, setWidgetAreasConfigurationState] = useState<WidgetAreasConfiguration>(widgetAreasConfiguration)
  const [loading, setLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()
  useTypedSelector(state => state.apiView)
  const { triggerAppRating } = useAppRatingContext()

  const close = (): void => {
    onClose?.()
    navigate(location.pathname.endsWith(EDIT_SEARCH_PARAMETER) ? location.pathname.replace(EDIT_SEARCH_PARAMETER, '') : location.pathname, {
      replace: true,
    })
  }

  const handleSave = async (
    dashboardAreasConfiguration: DashboardAreasConfiguration,
    widgetAreasConfiguration: WidgetAreasConfiguration
  ): Promise<void> => {
    try {
      setLoading(true)
      await saveAreasConfiguration(dashboardAreasConfiguration, widgetAreasConfiguration)
      enqueueSuccessStack(t('dashboard.editor.save-success'))
      dispatch(refreshApiView())
    } catch (err) {
      enqueueErrorStack(t('dashboard.editor.save-error'))
    } finally {
      setLoading(false)
      if (doNotShowAgain) setHideDashboardEditorInstructions(true)
      close()
      triggerAppRating()
    }
  }

  const actions: UpToThreeActions = [
    page === 0 || cookies.hideDashboardEditorInstructions
      ? { name: t('common.cancel'), onClick: () => close(), position: '1', style: 'secondary' }
      : { name: t('common.back'), onClick: () => setPage(Page.Instruction), position: '1', style: 'secondary' },
    page === 0 && !cookies.hideDashboardEditorInstructions
      ? { name: t('common.next'), onClick: () => setPage(Page.Editor), position: '2', style: 'primary', dataQa: DASHBOARD_EDITOR_NEXT_PAGE_BUTTON_ID }
      : {
          name: t('common.save'),
          onClick: () => handleSave(dashboardAreasConfigurationState, widgetAreasConfigurationState),
          position: '2',
          style: 'primary',
          loading,
          dataQa: DASHBOARD_EDITOR_SAVE_BUTTON_ID,
        },
  ]

  return (
    <Dialog actions={actions} open={true} title={t('dashboard.editor.title')} onClose={() => close()} fullScreen={true}>
      {page === Page.Instruction && !cookies.hideDashboardEditorInstructions ? (
        <InstructionsContent doNotShowAgain={doNotShowAgain} setDoNotShowAgain={setDoNotShowAgain} />
      ) : (
        <EditorContent
          dashboardAreasConfiguration={dashboardAreasConfigurationState}
          setDashboardAreasConfiguration={setDashboardAreasConfigurationState}
          widgetAreasConfiguration={widgetAreasConfigurationState}
          setWidgetAreasConfiguration={setWidgetAreasConfigurationState}
          isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
        />
      )}
    </Dialog>
  )
}

export default DashboardEditor
